import noop from 'lodash/noop';
import type { FunctionComponent } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useDebounceValue } from 'usehooks-ts';

import { useTranslate } from '~/shared/context/use-translate';
import { Button } from '~/ui/core/button/Button';
import { Checkbox } from '~/ui/core/checkbox/Checkbox';
import { Dialog } from '~/ui/core/dialog/Dialog';
import { IconArrowDown, IconArrowUp, IconCrossSmall, IconSearch } from '~/ui/core/icons';
import { Input } from '~/ui/core/input/Input';
import { Spinner } from '~/ui/core/spinner/Spinner';
import { TableCell, TableHeader, TableHeaderCell } from '~/ui/core/table/Table';

import { DocumentStatusBadge, useDocumentUploadEvents } from '../../company-settings';
import type { DocumentResponse } from '../../fetchers/documents/documents-fetchers';
import { useKnowledgeBaseDocuments } from '../api/use-knowledge-base-documents';
import { MAX_ATTACHMENT_COUNT, MAX_ATTACHMENT_SIZE_BYTES } from './PendingAttachmentList';

const SEARCH_DEBOUNCE_MS = 300;

type SortKey = 'name' | 'sizeBytes' | 'updatedAt';
type SortDirection = 'asc' | 'desc';

// The natural first click per column: names read A→Z, dates and sizes biggest/newest first.
const DEFAULT_SORT_DIRECTION: Record<SortKey, SortDirection> = {
    name: 'asc',
    sizeBytes: 'desc',
    updatedAt: 'desc',
};

export type SelectedKnowledgeBaseDocument = {
    readonly id: string;
    readonly name: string;
    readonly sizeBytes: number;
};

type KnowledgeBaseSelectDialogProps = {
    readonly isOpen: boolean;
    readonly onOpenChange: (open: boolean) => void;
    /** Receives the full knowledge-base selection — unchecked documents are meant to be detached. */
    readonly onAttach?: (documents: readonly SelectedKnowledgeBaseDocument[]) => void;
    /** Knowledge-base documents already attached in the composer; shown pre-checked on open. */
    readonly attachedDocuments?: readonly SelectedKnowledgeBaseDocument[];
    /** Uploads pending in the composer — they share the per-message count and size caps. */
    readonly pendingCount?: number;
    readonly pendingBytes?: number;
};

const SortableHeader: FunctionComponent<{
    readonly className: string;
    /** Set only on the active sort column. */
    readonly direction: SortDirection | undefined;
    readonly label: string;
    readonly onSort: () => void;
}> = ({ className, direction, label, onSort }) => (
    <span className={className}>
        <button
            className="flex cursor-pointer items-center gap-xxs border-none bg-transparent p-0 text-body-xs font-medium text-text-secondary hover:text-text-primary"
            type="button"
            onClick={onSort}
        >
            {label}
            {direction === 'asc' && <IconArrowUp size={12} aria-hidden={true} />}
            {direction === 'desc' && <IconArrowDown size={12} aria-hidden={true} />}
        </button>
    </span>
);

const formatFileSize = (sizeBytes: number | undefined): string => {
    if (sizeBytes === undefined) return '—';
    if (sizeBytes < 1024 * 1024) return `${Math.max(1, Math.round(sizeBytes / 1024))} KB`;
    const megabytes = sizeBytes / (1024 * 1024);
    const rounded = megabytes.toFixed(1);
    return `${rounded.endsWith('.0') ? Math.round(megabytes) : rounded} MB`;
};

const formatTotalMegabytes = (totalBytes: number): string => {
    if (totalBytes === 0) return '0';
    const megabytes = totalBytes / (1024 * 1024);
    const rounded = megabytes.toFixed(1);
    return rounded.endsWith('.0') ? String(Math.round(megabytes)) : rounded;
};

export const KnowledgeBaseSelectDialog: FunctionComponent<KnowledgeBaseSelectDialogProps> = ({
    isOpen,
    onOpenChange,
    onAttach,
    attachedDocuments = [],
    pendingCount = 0,
    pendingBytes = 0,
}) => {
    const translate = useTranslate();
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm] = useDebounceValue(searchTerm, SEARCH_DEBOUNCE_MS);
    const { data: documents, isError, isLoading } = useKnowledgeBaseDocuments(debouncedSearchTerm);
    useDocumentUploadEvents(noop, { enabled: isOpen });
    const [selected, setSelected] = useState<ReadonlyMap<string, SelectedKnowledgeBaseDocument>>(new Map());
    const attachedDocumentsRef = useRef(attachedDocuments);
    const [sortKey, setSortKey] = useState<SortKey>('updatedAt');

    useEffect(() => {
        attachedDocumentsRef.current = attachedDocuments;
    }, [attachedDocuments]);

    // Re-opening starts from what the composer already holds, so earlier picks show as checked
    // and unchecking one detaches it on Attach.
    useEffect(() => {
        if (isOpen) {
            setSelected(new Map(attachedDocumentsRef.current.map((document) => [document.id, document])));
        }
    }, [isOpen]);
    const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

    const handleSortBy = (key: SortKey): void => {
        if (key === sortKey) {
            setSortDirection((current) => (current === 'asc' ? 'desc' : 'asc'));
            return;
        }
        setSortKey(key);
        setSortDirection(DEFAULT_SORT_DIRECTION[key]);
    };

    const dateFormatter = useMemo(
        () => new Intl.DateTimeFormat(undefined, { day: 'numeric', month: 'short', year: 'numeric' }),
        [],
    );

    const totalSelectedBytes = useMemo(
        () => [...selected.values()].reduce((sum, document) => sum + document.sizeBytes, 0),
        [selected],
    );

    const handleOpenChange = (open: boolean): void => {
        onOpenChange(open);
        if (!open) {
            setSelected(new Map());
            setSearchTerm('');
        }
    };

    const toggleDocument = (document: DocumentResponse, checked: boolean): void => {
        setSelected((current) => {
            const next = new Map(current);
            if (checked) {
                next.set(document.id, {
                    id: document.id,
                    name: document.name,
                    sizeBytes: document.sizeBytes ?? 0,
                });
            } else {
                next.delete(document.id);
            }
            return next;
        });
    };

    const isRowSelectable = (document: DocumentResponse): boolean =>
        selected.has(document.id) ||
        (document.status === 'INDEXED' &&
            pendingCount + selected.size < MAX_ATTACHMENT_COUNT &&
            pendingBytes + totalSelectedBytes + (document.sizeBytes ?? 0) <= MAX_ATTACHMENT_SIZE_BYTES);

    const visibleDocuments = useMemo(() => {
        const compare = (a: DocumentResponse, b: DocumentResponse): number => {
            switch (sortKey) {
                case 'name':
                    return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
                case 'sizeBytes':
                    return (a.sizeBytes ?? 0) - (b.sizeBytes ?? 0);
                case 'updatedAt':
                    return Date.parse(a.updatedAt) - Date.parse(b.updatedAt);
            }
        };
        const sorted = [...(documents ?? [])].sort(compare);
        return sortDirection === 'desc' ? sorted.reverse() : sorted;
    }, [documents, sortDirection, sortKey]);

    const handleAttach = (): void => {
        onAttach?.([...selected.values()]);
        handleOpenChange(false);
    };

    return (
        <Dialog.Root open={isOpen} onOpenChange={handleOpenChange}>
            <Dialog.Portal>
                <Dialog.Overlay style={{ zIndex: 1206 }} />
                <Dialog.Content
                    className="flex w-[min(900px,92vw)] max-w-none flex-col gap-l max-h-[85vh] z-[1207]"
                    role="dialog"
                >
                    <div className="flex items-center justify-between">
                        <Dialog.Title className="m-0 text-h4 font-medium text-text-form">
                            {translate('pages.job-architecture-v2.create-items.upload.select-from-knowledge-base')}
                        </Dialog.Title>
                        <Dialog.Close asChild={true}>
                            <button
                                aria-label={translate('common.close')}
                                className="flex cursor-pointer items-center border-none bg-transparent p-0 text-text-content-faint hover:text-text-primary"
                                type="button"
                            >
                                <IconCrossSmall size={16} />
                            </button>
                        </Dialog.Close>
                    </div>
                    <Input
                        placeholder={translate('pages.job-architecture-v2.knowledge-base.search-placeholder')}
                        startElement={<IconSearch size={16} />}
                        value={searchTerm}
                        variant="search"
                        onChange={(event) => setSearchTerm(event.target.value)}
                    />
                    <div className="min-h-0 overflow-x-auto rounded-lg border border-border-subtle bg-surface-canvas">
                        {/* biome-ignore lint/a11y/useSemanticElements: rows and cells use the shared Table primitive's CSS grid layout. */}
                        <div
                            aria-label={translate('pages.job-architecture-v2.knowledge-base.table-title')}
                            className="min-w-[680px]"
                            role="table"
                        >
                            <TableHeader className="grid-cols-[48px_minmax(0,1fr)_144px_136px_96px] rounded-none border-b border-border-subtle">
                                <TableHeaderCell />
                                <TableHeaderCell>
                                    <SortableHeader
                                        className="min-w-0"
                                        direction={sortKey === 'name' ? sortDirection : undefined}
                                        label={translate('common.document')}
                                        onSort={() => handleSortBy('name')}
                                    />
                                </TableHeaderCell>
                                <TableHeaderCell>{translate('common.status')}</TableHeaderCell>
                                <TableHeaderCell>
                                    <SortableHeader
                                        className="min-w-0"
                                        direction={sortKey === 'updatedAt' ? sortDirection : undefined}
                                        label={translate('common.last-modified')}
                                        onSort={() => handleSortBy('updatedAt')}
                                    />
                                </TableHeaderCell>
                                <TableHeaderCell>
                                    <SortableHeader
                                        className="min-w-0"
                                        direction={sortKey === 'sizeBytes' ? sortDirection : undefined}
                                        label={translate('common.size')}
                                        onSort={() => handleSortBy('sizeBytes')}
                                    />
                                </TableHeaderCell>
                            </TableHeader>
                            {/* biome-ignore lint/a11y/useSemanticElements: rows use the shared Table primitive's CSS grid layout. */}
                            <div className="max-h-[400px] min-h-0 overflow-y-auto" role="rowgroup">
                                {visibleDocuments.map((document) => (
                                    // biome-ignore lint/a11y/useSemanticElements: rows are CSS-grid tracks, matching the shared Table primitive.
                                    // biome-ignore lint/a11y/useFocusableInteractive: the checkbox owns keyboard focus.
                                    <div
                                        key={document.id}
                                        className={`grid grid-cols-[48px_minmax(0,1fr)_144px_136px_96px] border-b border-border-subtle last:border-b-0 hover:bg-surface-hover ${selected.has(document.id) ? 'bg-surface-hover' : ''}`}
                                        role="row"
                                    >
                                        <TableCell className="px-l" role="cell">
                                            <Checkbox
                                                aria-label={document.name}
                                                checked={selected.has(document.id)}
                                                disabled={!isRowSelectable(document)}
                                                onCheckedChange={(checked) =>
                                                    toggleDocument(document, checked === true)
                                                }
                                            />
                                        </TableCell>
                                        <TableCell className="min-w-0 text-body-s text-text-primary" role="cell">
                                            <span className="truncate">{document.name}</span>
                                        </TableCell>
                                        <TableCell role="cell">
                                            <DocumentStatusBadge status={document.status} />
                                        </TableCell>
                                        <TableCell className="text-body-xs text-text-muted" role="cell">
                                            {dateFormatter.format(new Date(document.updatedAt))}
                                        </TableCell>
                                        <TableCell className="text-body-xs text-text-muted" role="cell">
                                            {formatFileSize(document.sizeBytes)}
                                        </TableCell>
                                    </div>
                                ))}
                            </div>
                        </div>
                        {isLoading && (
                            <div className="flex justify-center py-xl">
                                <Spinner />
                            </div>
                        )}
                        {isError && (
                            <p className="m-0 py-xl text-center text-body-xs text-text-muted">
                                {translate('chat.error-fallback')}
                            </p>
                        )}
                        {!isLoading && !isError && visibleDocuments.length === 0 && (
                            <p className="m-0 py-xl text-center text-body-xs text-text-muted">
                                {translate('pages.job-architecture-v2.knowledge-base.no-documents')}
                            </p>
                        )}
                    </div>
                    <div className="flex flex-wrap items-center justify-between gap-l border-t border-border-subtle pt-l">
                        <span className="text-body-s text-text-secondary">
                            {translate('pages.job-architecture-v2.create-items.upload.selection-summary', {
                                // The caps shrink by what is already pending in the composer, so the
                                // footer reflects the capacity this dialog can still fill.
                                maxCount: MAX_ATTACHMENT_COUNT - pendingCount,
                                maxSize: formatTotalMegabytes(MAX_ATTACHMENT_SIZE_BYTES - pendingBytes),
                                selectedCount: selected.size,
                                size: formatTotalMegabytes(totalSelectedBytes),
                            })}
                        </span>
                        <div className="flex items-center gap-s">
                            <Button size="sm" variant="tertiary" onClick={() => handleOpenChange(false)}>
                                {translate('common.cancel')}
                            </Button>
                            <Button
                                disabled={selected.size === 0 && attachedDocuments.length === 0}
                                disabledVariant="dark"
                                size="sm"
                                variant="primary"
                                onClick={handleAttach}
                            >
                                {translate('chat.attach')}
                            </Button>
                        </div>
                    </div>
                </Dialog.Content>
            </Dialog.Portal>
        </Dialog.Root>
    );
};
